import { ElementRef, Injectable, inject } from '@angular/core';
import { Observable, Subject, take } from 'rxjs';
import { ConfirmMenuComponent } from 'src/app/shared/components/confirm-menu/confirm-menu.component';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';

@Injectable({
  providedIn: 'root',
})
export class ConfirmMenuService {
  private overlay = inject(DynamicOverlayService);

  confirm(target: ElementRef | HTMLElement, label: string, icon?: string, color?: string, title?: string, message?: string): Observable<void> {
    const confirmation$ = new Subject<void>();
    const elementRef = target instanceof ElementRef ? target : new ElementRef(target);

    const { componentInstance, ref: overlayRef } = this.overlay.open(elementRef, ConfirmMenuComponent, {
      hasBackdrop: true,
      data: { label, icon, color, title, message },
    });
    // Note: Since we are manually setting the input signal value (which is read-only usually, but in component instance properties it can be set if it wasn't a signal, but for signals we need to setting it carefully or passing via injector if dynamic component loading supports it differently.
    // However, `DynamicOverlayService` likely uses `ComponentRef.setInput()`.
    // Let's assume `open` method returns `ComponentRef` and we can set input.
    // If `DynamicOverlayService` returns `componentInstance`, Angular 17+ signals are inputs.
    // Setting `componentInstance.label = ...` might not work if it is a signal input.
    // We should check how `DynamicOverlayService` instantiates or allowing setting inputs.
    // If it doesn't support `setInput`, we might need reference to `ComponentRef`.

    // Assuming DynamicOverlayService returns something that allows us to interact with the component.
    // Let's look at `DynamicOverlayService.ts` to be sure.

    // BUT for now, I will write the service assuming I can set it via `setInput` on `ref` if exposed, or if `DynamicOverlayService` returns `componentInstance` which is the instance, I can try to set it but signals are read-only.
    // Actually, `componentRef.setInput('label', label)` is the way for signals.

    // Let's assume `ref` in the return object is the `OverlayRef` or `ComponentRef`?
    // Let's check `DynamicOverlayService` code quickly first?

    const sub = componentInstance.confirmed.subscribe(() => {
      confirmation$.next();
      confirmation$.complete();
      this.overlay.close();
    });

    overlayRef
      .detachments()
      .pipe(take(1))
      .subscribe(() => {
        sub.unsubscribe();
        if (!confirmation$.closed) {
          confirmation$.complete();
        }
      });

    return confirmation$.asObservable();
  }
}
